import { useEffect, useMemo, useRef } from "react";
import { Control, useFormState, useWatch } from "react-hook-form";
import * as yup from "yup";
import { bookingProcessApi } from "./store";
import { BookingProcessStore } from "./types";

/**
 * Hook to collect empty or invalid required fields from a react-hook-form form instance.
 * Useful for displaying to users which required fields need to be filled or corrected.
 *
 * Includes fields that are:
 * - Empty (null, undefined, empty string, empty array, empty object)
 * - Invalid (have validation errors, e.g., invalid email format, invalid phone number)
 *
 * @template TFormValues - The form values type
 * @param control - React Hook Form control instance
 * @param fieldLabels - Mapping of field names to their display labels
 * @param options - Optional configuration
 * @param options.hiddenFields - Array of field names to exclude from checking
 * @param options.trigger - Optional function to trigger validation (from useForm().trigger). If provided, validation will run on mount to detect invalid fields immediately.
 * @returns Object containing array of empty or invalid required fields with their names and labels
 *
 * @example
 * ```tsx
 * const { control } = useForm<FormData>({...});
 *
 * const { emptyFields } = useEmptyRequiredFields(
 *   control,
 *   {
 *     firstName: "First Name",
 *     lastName: "Last Name",
 *     email: "Email",
 *     phone: "Phone",
 *   },
 *   {
 *     hiddenFields: ["emailSubscribe"], // Optional: exclude optional fields
 *   }
 * );
 *
 * // emptyFields will include both empty fields and invalid fields (e.g., invalid email format)
 * // Example: [{ name: "firstName", label: "First Name" }, { name: "email", label: "Email" }]
 * ```
 */
export const useEmptyRequiredFields = <TFormValues extends Record<string, any>>(
  control: Control<TFormValues>,
  fieldLabels: Partial<Record<keyof TFormValues, string>>,
  options?: {
    hiddenFields?: (keyof TFormValues)[];
    trigger?: (
      name?: keyof TFormValues | (keyof TFormValues)[],
    ) => Promise<boolean>;
    /** Yup schema for direct validation (bypasses clearErrors interference) */
    validationSchema?: yup.ObjectSchema<any>;
  },
) => {
  const formValues = useWatch({ control });
  const { errors } = useFormState({ control });
  const hasTriggeredValidation = useRef(false);
  const trigger = options?.trigger;

  const emptyFields = useMemo(() => {
    // Do NOT gate on isValid — clearErrors() in SchemaFormRenderer
    // temporarily sets isValid=true even when required fields are empty.

    // Validate directly against the Yup schema so we catch both empty
    // and invalid fields (e.g. bad phone format) even after clearErrors()
    // wipes the react-hook-form errors object.
    const schemaErrors = new Set<string>();
    if (options?.validationSchema) {
      try {
        options.validationSchema.validateSync(formValues, {
          abortEarly: false,
        });
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          for (const inner of err.inner) {
            if (inner.path) schemaErrors.add(inner.path);
          }
        }
      }
    }

    const hiddenFieldsSet = new Set(options?.hiddenFields || []);
    const emptyRequiredFields: Array<{
      name: keyof TFormValues;
      label: string;
    }> = [];

    // Check each field that has a label mapping
    Object.keys(fieldLabels).forEach((fieldName) => {
      const fieldKey = fieldName as keyof TFormValues;

      // Skip hidden fields
      if (hiddenFieldsSet.has(fieldKey)) {
        return;
      }

      const fieldError = (errors as Record<string, unknown>)?.[fieldName];
      const fieldValue = (formValues as Record<string, unknown>)?.[fieldName];

      // Field fails: react-hook-form error OR direct schema error OR empty value
      const hasValidationError = !!fieldError || schemaErrors.has(fieldName);

      const isEmpty =
        fieldValue === null ||
        fieldValue === undefined ||
        fieldValue === "" ||
        (typeof fieldValue === "string" && fieldValue.trim() === "") ||
        (Array.isArray(fieldValue) && fieldValue.length === 0) ||
        (typeof fieldValue === "object" &&
          fieldValue !== null &&
          !Array.isArray(fieldValue) &&
          Object.keys(fieldValue).length === 0);

      if (hasValidationError || isEmpty) {
        const label = fieldLabels[fieldKey];
        if (label != null) {
          emptyRequiredFields.push({
            name: fieldKey,
            label: label || String(fieldKey),
          });
        }
      }
    });

    return emptyRequiredFields;
  }, [
    formValues,
    errors,
    fieldLabels,
    options?.hiddenFields,
    options?.validationSchema,
  ]);

  // Check if form has any values (non-empty)
  // Excludes boolean fields as they don't indicate meaningful user input
  const hasFormValues = useMemo(() => {
    return Object.values(formValues || {}).some((value) => {
      // Exclude boolean fields
      if (typeof value === "boolean") {
        return false;
      }
      if (value === null || value === undefined || value === "") {
        return false;
      }
      if (typeof value === "string" && value.trim() === "") {
        return false;
      }
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }
      if (
        typeof value === "object" &&
        !Array.isArray(value) &&
        Object.keys(value).length === 0
      ) {
        return false;
      }
      return true;
    });
  }, [formValues]);

  // Trigger validation on mount to detect invalid fields immediately
  // Only trigger if form has values (to avoid showing errors on empty forms)
  useEffect(() => {
    if (trigger && !hasTriggeredValidation.current && hasFormValues) {
      hasTriggeredValidation.current = true;
      // Trigger validation for all fields that have labels
      const fieldNames = Object.keys(fieldLabels) as (keyof TFormValues)[];
      trigger(fieldNames).catch(() => {
        // Ignore validation errors - we just want to populate the errors object
      });
    }
  }, []);

  useEffect(() => {
    bookingProcessApi.setEmptyFields(
      emptyFields as BookingProcessStore["emptyFields"],
    );
  }, [emptyFields]);

  return { emptyFields };
};
